import { createGuid } from '@bimser/core';
import { List, Skeleton } from 'antd';
import * as React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { BCConfigProvider, BCEmpty, BCScrollbars, BCSkeleton } from '../';
import { ListItem } from './ListItem';
import Styles from './assets/listStyles.scss';
import IListGridType from './entities/IListGridType';
import IListPagination from './entities/IListPagination';
import IListProps from './entities/IListProps';

const BCList = (props: IListProps) => {
	let infinityScrollContainerId: string;

	React.useEffect(() => {
		infinityScrollContainerId = createGuid();
	}, []);

	Styles;

	const renderItem = (itemProps: any, index: number) => {
		let itemKey = index.toString();
		if (itemProps?.id !== null && itemProps?.id !== undefined) {
			itemKey += itemProps.id;
		}
		if (props.sortable) {
			return (
				<ListItem
					key={itemKey}
					id={itemKey}
					index={index}
					itemProps={itemProps}
					moveCard={moveItem}
					dropItem={dropItem}
					renderItem={props.renderItem}
				/>
			);
		} else {
			return (
				<List.Item
					id={itemProps.id}
					key={itemKey}
					onClick={props.onClickListItem}
				>
					{props.renderItem(itemProps)}
				</List.Item>
			);
		}
	};

	const customizeRenderEmpty = () => {
		if (props.customEmpty) {
			return props.customEmpty;
		} else {
			return <BCEmpty />;
		}
	};
	const moveItem = (dragIndex: number, hoverIndex: number) => {
		if (props.moveItem) {
			props.moveItem(dragIndex, hoverIndex);
		}
	};
	const dropItem = (dragIndex: number, hoverIndex: number) => {
		if (props.dropItem) {
			props.dropItem(dragIndex, hoverIndex);
		}
	};

	const getList = () => (
		<List
			pagination={props.pagination ? { ...props.pagination } : false}
			header={props.header}
			footer={props.footer}
			bordered={props.bordered}
			dataSource={props.dataSource}
			extra={props.extra}
			grid={props.grid}
			itemLayout={props.itemLayout}
			loading={props.loading}
			loadMore={props.loadMore}
			locale={props.locale}
			prefixCls={props.prefixCls}
			rowKey={props.rowKey}
			size={props.size}
			split={props.split}
			renderItem={renderItem}
			className={props.className}
		/>
	);

	const getLoadingSkeleton = () => {
		if (props.dataSource.length !== props.totalCount) {
			if (props.loadingSkeletonContent) {
				return (
					<BCSkeleton
						contentType={props.loadingSkeletonContent}
						height={props.loadingSkeletonHeight}
						loadingStatus={props.loadingSkeletonShow}
					/>
				);
			}

			return (
				<Skeleton
					paragraph={{ rows: 3 }}
					active
				/>
			);
		}

		return <></>;
	};

	const getInfinityScroll = () => (
		<BCScrollbars
			styles={{ height: props.scrollHeight }}
			onScroll={props.onScrollInfinityScroll}
		>
			<List
				pagination={props.pagination ? { ...props.pagination } : false}
				header={props.header}
				footer={props.footer}
				bordered={props.bordered}
				extra={props.extra}
				grid={props.grid}
				itemLayout={props.itemLayout}
				loading={props.loading}
				loadMore={props.loadMore}
				locale={props.locale}
				prefixCls={props.prefixCls}
				rowKey={props.rowKey}
				size={props.size}
				split={props.split}
				renderItem={renderItem}
				className={props.className}
			>
				{props.dataSource.map((item, index) => renderItem(item, index))}
			</List>
			{getLoadingSkeleton()}
		</BCScrollbars>
	);

	const renderList = () => {
		if (props.infinityScroll) {
			return getInfinityScroll();
		}

		return <BCConfigProvider renderEmpty={customizeRenderEmpty}>{getList()}</BCConfigProvider>;
	};

	if (props.sortable) {
		return <DndProvider backend={HTML5Backend}>{renderList()}</DndProvider>;
	} else {
		return renderList();
	}
};

export { BCList, IListGridType, IListPagination, IListProps };
